import { Alert, Cell, FormControl, Grid, HFlow, InfoLabel, Paper, Text, Theme, useStyles, VFlow } from 'bold-ui'
import { FormSectionAccordion } from 'components/accordion/FormSectionAccordion'
import { DecimalField, ErrorField, NumberField, RadioField } from 'components/form'
import { resolveValue } from 'components/form/final-form/useField'
import { TipoColetaGlicemia } from 'graphql/types.generated'
import React, { useMemo } from 'react'
import { useFormState } from 'react-final-form'
import { tipoColetaGlicemia } from 'types/enums'
import { calcularIMC, calcularRiscoCardiovascular, estadoNutricional } from 'util/atendimento'
import { MetaPath, MetaRoot } from 'util/metaPath'
import { formatNumber, parseNumber } from 'util/number'
import { isEmpty } from 'util/validation/Util'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'

export interface MedicoesPanelModel {
  openedAccordions?: (string | number)[]
  peso?: string
  altura?: string
  perimetroCefalico?: string
  circunferenciaAbdominal?: string
  perimetroPanturrilha?: string
  pressaoArterialSistolica?: string
  pressaoArterialDiastolica?: string
  frequenciaRespiratoria?: string
  frequenciaCardiaca?: string
  temperatura?: string
  saturacaoO2?: string
  glicemia?: string
  tipoGlicemia?: TipoColetaGlicemia
}

export interface MedicoesPanelProps {
  name: MetaPath<MedicoesPanelModel> | MetaRoot<MedicoesPanelModel>
  cidadao: CidadaoAtendimento
}

export function MedicoesPanel(props: MedicoesPanelProps) {
  const { name, cidadao } = props

  const { values: formValues } = useFormState({ subscription: { values: true } })

  const values = resolveValue(formValues, name) || {}

  const { classes, theme } = useStyles(createStyles)

  const alertMessageAbdominal = useMemo(
    () => calcularRiscoCardiovascular(cidadao.sexo, parseNumber(values.circunferenciaAbdominal)),
    [cidadao.sexo, values.circunferenciaAbdominal]
  )

  const imc = useMemo(() => calcularIMC(parseNumber(values.peso), parseNumber(values.altura)), [
    values.peso,
    values.altura,
  ])

  const msgEstadoNutricional = useMemo(
    () => imc && estadoNutricional(imc, cidadao.sexo, cidadao.idade, cidadao.gestacoes, cidadao.identidadeGeneroDbEnum),
    [imc, cidadao.sexo, cidadao.idade, cidadao.gestacoes, cidadao.identidadeGeneroDbEnum]
  )

  return (
    <FormSectionAccordion
      name={name.openedAccordions}
      title='Antropometria, sinais vitais e glicemia capilar'
      uuid='medicoes'
    >
      <div style={{ padding: '1rem' }}>
        <Grid alignItems='flex-start'>
          <Cell size={7}>
            <Paper style={{ padding: '0.625rem' }}>
              <Grid gap={1} alignItems='flex-end' justifyContent='flex-start'>
                <Cell xs={12} lg={8} flexGrow={1}>
                  <HFlow>
                    <DecimalField name={name.peso} label='Peso (kg)' maxLength={7} precisao={3} />
                    <DecimalField name={name.altura} label='Altura (cm)' maxLength={5} precisao={2} />
                    <VFlow>
                      <InfoLabel title='IMC'>
                        <Text component='span' fontSize={1.5} fontWeight='bold' fontStyle='normal'>
                          {imc ? formatNumber(imc) : '-'}
                        </Text>
                      </InfoLabel>
                    </VFlow>
                  </HFlow>
                </Cell>
                {msgEstadoNutricional && (
                  <Cell xs={12} lg={4}>
                    <Alert inline type={msgEstadoNutricional.alertType}>
                      {msgEstadoNutricional.message}
                    </Alert>
                  </Cell>
                )}
              </Grid>
            </Paper>
          </Cell>

          <Cell
            lg={1}
            style={{
              [theme.breakpoints.down('lg')]: {
                display: 'none',
              },
            }}
          />

          <Cell xs={4} lg={3}>
            <DecimalField name={name.perimetroCefalico} label='Perímetro cefálico (cm)' maxLength={5} precisao={1} />
          </Cell>

          <Cell xs={6} lg={7}>
            <Grid gap={1} alignItems='flex-end'>
              <Cell xs={9} lg={5}>
                <DecimalField
                  name={name.circunferenciaAbdominal}
                  label='Circunferência abdominal (cm)'
                  maxLength={5}
                  precisao={1}
                />
              </Cell>
              {alertMessageAbdominal && (
                <Cell xs={12} lg={5}>
                  <Alert inline type='warning'>
                    {alertMessageAbdominal}
                  </Alert>
                </Cell>
              )}
            </Grid>
          </Cell>

          <Cell size={1} />

          <Cell xs={4} lg={3}>
            <DecimalField
              name={name.perimetroPanturrilha}
              label='Perímetro da panturrilha (cm)'
              maxLength={5}
              precisao={1}
            />
          </Cell>
        </Grid>

        <Grid style={classes.border}>
          <Cell xs={12} lg={4}>
            <FormControl label='Pressão arterial (mmHg)'>
              <HFlow>
                <NumberField name={name.pressaoArterialSistolica} label='' maxLength={3} />
                <Text component='p' fontSize={1.25}>
                  /
                </Text>
                <NumberField name={name.pressaoArterialDiastolica} label='' maxLength={3} />
              </HFlow>
            </FormControl>
          </Cell>

          <Cell xs={4} lg={3}>
            <NumberField name={name.frequenciaRespiratoria} label='Frequência respiratória (mpm)' maxLength={3} />
          </Cell>

          <Cell xs={3} lg={1} />

          <Cell xs={4} lg={3}>
            <NumberField name={name.frequenciaCardiaca} label='Frequência cardíaca (bpm)' maxLength={3} />
          </Cell>

          <Cell xs={4} lg={3}>
            <DecimalField name={name.temperatura} label='Temperatura (°C)' maxLength={4} precisao={2} />
          </Cell>

          <Cell xs={3} lg={1} />

          <Cell xs={4} lg={3}>
            <NumberField name={name.saturacaoO2} label='Saturação de O2 (%)' maxLength={3} />
          </Cell>
        </Grid>

        <Grid style={classes.border}>
          <Cell size={4}>
            <NumberField name={name.glicemia} label='Glicemia capilar (mg/dL)' maxLength={3} />
          </Cell>

          <Cell xs={12} lg={8}>
            <FormControl label='Momento da coleta' required={!isEmpty(values.glicemia)}>
              <VFlow vSpacing={0.5}>
                <HFlow style={{ marginLeft: '-0.25rem' }}>
                  <RadioField
                    name={name.tipoGlicemia}
                    label={tipoColetaGlicemia[TipoColetaGlicemia.NAO_ESPECIFICADO]}
                    value={TipoColetaGlicemia.NAO_ESPECIFICADO}
                    disabled={!values.glicemia}
                  />
                  <RadioField
                    name={name.tipoGlicemia}
                    label={tipoColetaGlicemia[TipoColetaGlicemia.JEJUM]}
                    value={TipoColetaGlicemia.JEJUM}
                    disabled={!values.glicemia}
                  />
                  <RadioField
                    name={name.tipoGlicemia}
                    label={tipoColetaGlicemia[TipoColetaGlicemia.PREPRANDIAL]}
                    value={TipoColetaGlicemia.PREPRANDIAL}
                    disabled={!values.glicemia}
                  />
                  <RadioField
                    name={name.tipoGlicemia}
                    label={tipoColetaGlicemia[TipoColetaGlicemia.POSPRANDIAL]}
                    value={TipoColetaGlicemia.POSPRANDIAL}
                    disabled={!values.glicemia}
                  />
                </HFlow>
                <ErrorField name={name.tipoGlicemia} />
              </VFlow>
            </FormControl>
          </Cell>
        </Grid>
      </div>
    </FormSectionAccordion>
  )
}

const createStyles = (theme: Theme) => ({
  border: {
    borderTop: `0.063rem solid ${theme.pallete.gray.c80}`,
    marginTop: '1rem',
  },
})
